import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * AdminCrud
 * props:
 *  endpoint: string like "/events" (also used for /events/{id})
 *  singular: string label for one item ("event")
 *  listColumns: [{ key, label, format? }]
 *  fields: [{ key, label, type: "text"|"textarea"|"number"|"date"|"select", required?, options?, colSpan? }]
 *  testIdPrefix: string, e.g. "admin-events"
 *  fetchQuery: optional axios query params for GET
 */
export default function AdminCrud({ endpoint, singular, listColumns, fields, testIdPrefix, fetchQuery }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new, object=edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(endpoint, { params: fetchQuery })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [endpoint]);

  const openNew = () => {
    const empty = {};
    fields.forEach((f) => { empty[f.key] = f.type === "number" ? 0 : ""; });
    setForm(empty);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const filled = {};
    fields.forEach((f) => { filled[f.key] = row[f.key] ?? (f.type === "number" ? 0 : ""); });
    setForm(filled);
    setEditing(row);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key] ?? 0); });
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success(`${singular} updated`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${singular} created`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleteId}`);
      toast.success(`${singular} deleted`);
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--azsam-ink-soft)]">{items.length} {singular}{items.length === 1 ? "" : "s"}</div>
        <button className="btn-primary !py-2 !px-4 text-sm" onClick={openNew} data-testid={`${testIdPrefix}-new`}>
          <Plus size={16} /> New {singular}
        </button>
      </div>

      <div className="azsam-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--azsam-cream)] text-[color:var(--azsam-ink-soft)] uppercase tracking-widest text-xs">
              <tr>
                {listColumns.map((c) => (
                  <th key={c.key} className="text-left px-5 py-3 font-semibold">{c.label}</th>
                ))}
                <th className="px-5 py-3 text-right w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={listColumns.length + 1} className="text-center py-8 text-[color:var(--azsam-ink-soft)]">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={listColumns.length + 1} className="text-center py-8 text-[color:var(--azsam-ink-soft)]">No {singular}s yet.</td></tr>
              )}
              {items.map((row) => (
                <tr key={row.id} className="border-t border-[color:var(--azsam-stone)] hover:bg-[color:var(--azsam-cream)]/60" data-testid={`${testIdPrefix}-row-${row.id}`}>
                  {listColumns.map((c) => (
                    <td key={c.key} className="px-5 py-3 align-top max-w-[320px]">
                      <div className="line-clamp-2">{c.format ? c.format(row[c.key], row) : (row[c.key] ?? "—")}</div>
                    </td>
                  ))}
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button className="p-2 rounded hover:bg-white border border-[color:var(--azsam-stone)] mr-2" onClick={() => openEdit(row)} title="Edit" data-testid={`${testIdPrefix}-edit-${row.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button className="p-2 rounded hover:bg-white border border-[color:var(--azsam-stone)] text-[color:var(--azsam-mesa)]" onClick={() => setDeleteId(row.id)} title="Delete" data-testid={`${testIdPrefix}-delete-${row.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? `Edit ${singular}` : `New ${singular}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : ""}>
                  <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-1.5 font-semibold">
                    {f.label}{f.required && <span className="text-[color:var(--azsam-clay)]"> *</span>}
                  </div>
                  {f.type === "textarea" ? (
                    <textarea
                      required={f.required}
                      rows={f.rows || 4}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] resize-y"
                      data-testid={`${testIdPrefix}-field-${f.key}`}
                    />
                  ) : f.type === "select" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                      <SelectTrigger className="h-10 rounded-md border-[color:var(--azsam-stone)]" data-testid={`${testIdPrefix}-field-${f.key}`}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input
                      required={f.required}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      step={f.step}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)]"
                      data-testid={`${testIdPrefix}-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter className="pt-2">
              <button type="button" className="btn-outline !py-2 !px-4 text-sm" onClick={() => setDialogOpen(false)} data-testid={`${testIdPrefix}-cancel`}>
                <X size={14} /> Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm disabled:opacity-60" data-testid={`${testIdPrefix}-save`}>
                <Save size={14} /> {saving ? "Saving…" : editing ? "Save changes" : `Create ${singular}`}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid={`${testIdPrefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {singular}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[color:var(--azsam-mesa)] hover:bg-[color:var(--azsam-mesa)]/90" data-testid={`${testIdPrefix}-delete-confirm`}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
